// src/utils/apiError.js
// Helpers for turning a FastAPI error `detail` into something safe to render.
//
// FastAPI returns `detail` as a plain string for most errors, but as an ARRAY
// of objects — {type, loc, msg, input, ctx} — for 422 validation errors.
// Rendering that object directly as a React child throws "React error #31"
// (Objects are not valid as a React child) and white-screens the page.

/**
 * Coerce an API error `detail` (string | array | object | null) into a
 * human-readable string.
 */
export function apiErrorMessage(detail, fallback = 'Something went wrong. Please try again.') {
  if (detail === null || detail === undefined || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === 'string' ? d : d && (d.msg || d.detail || d.message)))
      .filter(Boolean);
    return msgs.length ? msgs.join(', ') : fallback;
  }
  if (typeof detail === 'object') return detail.msg || detail.detail || detail.message || fallback;
  return String(detail) || fallback;
}

/**
 * Normalize an axios error in place so no call site can ever render a raw
 * validation-error object. If `response.data.detail` is not a string, the
 * original (structured) value is preserved on `response.data.detail_errors`
 * for the rare consumer that needs the per-field breakdown, and
 * `response.data.detail` is replaced with a safe display string.
 */
export function normalizeApiError(error) {
  const data = error && error.response && error.response.data;
  if (!data) return error;
  const detail = data.detail;
  if (detail != null && typeof detail !== 'string') {
    data.detail_errors = detail;            // preserve structured 422 field errors
    data.detail = apiErrorMessage(detail);  // safe, renderable string
  }
  return error;
}

/**
 * Read an API error's STRUCTURED `detail` — the object/array the backend
 * actually sent, not the display string.
 *
 * Any code branching on a machine-readable error code must go through this,
 * e.g. `apiErrorDetail(err)?.error === 'terms_acceptance_required'`. Reading
 * `response.data.detail` directly does NOT work: normalizeApiError() above
 * runs on every response via the shared axios interceptor and has already
 * replaced a non-string `detail` with a rendering-safe string, moving the
 * original to `detail_errors`. A check against the stringified value silently
 * never matches, so the gate it guards (a terms modal, a capability gate)
 * simply never opens and the user sees a dead-end error instead.
 */
export function apiErrorDetail(error) {
  const data = error && error.response && error.response.data;
  if (!data) return null;
  return data.detail_errors !== undefined ? data.detail_errors : data.detail;
}
